import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Trash2 } from "lucide-react";
import { Toaster, toast } from "sonner";
import AppointmentCard from "../components/AppointmentCard";
import EmptyState from "../components/EmptyState";
import { listHistory, deleteAllHistory } from "../lib/api";

export default function HistoryPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    try {
      const data = await listHistory();
      setItems(data);
    } catch {
      toast.error("Couldn't load history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleDeleteAll = async () => {
    setDeleting(true);
    try {
      const res = await deleteAllHistory();
      toast.success(`Cleared ${res.deleted_count} past reservations`);
      setItems([]);
      setConfirmingDelete(false);
    } catch {
      toast.error("Couldn't clear history");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="app-shell">
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            background: "rgba(18,18,23,0.95)",
            color: "#F5F5F5",
            border: "1px solid rgba(212,175,55,0.25)",
            backdropFilter: "blur(12px)",
            fontFamily: "Manrope, sans-serif",
          },
        }}
      />

      <header
        className="sticky top-0 z-40 backdrop-blur-xl"
        style={{
          background: "rgba(5, 5, 8, 0.7)",
          borderBottom: "1px solid rgba(212, 175, 55, 0.08)",
        }}
      >
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between gap-3">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#F3E5AB] transition-colors text-xs uppercase tracking-[0.2em]"
            data-testid="back-to-home"
          >
            <ArrowLeft size={14} /> Home
          </Link>
          <p className="eyebrow">History</p>
          <div className="w-16" />
        </div>
      </header>

      <section className="max-w-3xl mx-auto px-6 pt-12 pb-6">
        <p className="eyebrow">Past Reservations</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#F5F5F5] mt-3 leading-tight">
          The moments <span className="italic text-[#D4AF37]">behind you</span>
        </h1>
        <p className="text-[#8E8E93] mt-4 max-w-lg">
          A record of what's been. Keep what matters, clear what doesn't.
        </p>

        {items.length > 0 && (
          <div className="mt-6">
            {!confirmingDelete ? (
              <button
                onClick={() => setConfirmingDelete(true)}
                className="btn-ghost-gold rounded-full inline-flex items-center gap-2 px-4 py-2 text-xs tracking-[0.2em] uppercase"
                style={{ borderColor: "rgba(255,69,58,0.4)", color: "#FF453A" }}
                data-testid="clear-history-btn"
              >
                <Trash2 size={13} /> Clear all history
              </button>
            ) : (
              <div
                className="inline-flex items-center gap-3 px-4 py-2 rounded-full"
                style={{ background: "rgba(255,69,58,0.08)", border: "1px solid rgba(255,69,58,0.35)" }}
                data-testid="confirm-clear-history"
              >
                <span className="text-xs uppercase tracking-[0.2em] text-[#FF453A]">
                  Delete all {items.length}?
                </span>
                <button
                  onClick={handleDeleteAll}
                  disabled={deleting}
                  className="text-xs uppercase tracking-[0.2em] text-[#FF453A] font-medium hover:text-white transition-colors disabled:opacity-50"
                  data-testid="confirm-clear-yes"
                >
                  {deleting ? "Clearing…" : "Yes, delete"}
                </button>
                <button
                  onClick={() => setConfirmingDelete(false)}
                  className="text-xs uppercase tracking-[0.2em] text-[#8E8E93] hover:text-white transition-colors"
                  data-testid="confirm-clear-cancel"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}
      </section>

      <main className="max-w-3xl mx-auto px-6 pb-32" data-testid="history-main">
        {loading ? (
          <p className="text-center text-[#8E8E93] text-sm">Composing your record…</p>
        ) : items.length === 0 ? (
          <EmptyState label="No past reservations yet." />
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {items.map((appt) => (
              <AppointmentCard key={appt.id} appt={appt} />
            ))}
          </div>
        )}
      </main>

      <footer className="max-w-3xl mx-auto px-6 pb-10 text-center">
        <div className="gold-hairline w-24 mx-auto mb-4" />
        <p className="text-[10px] tracking-[0.3em] uppercase text-[#8E8E93]">
          MF · Private Edition
        </p>
      </footer>
    </div>
  );
}
